import Axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { ERROR_MESSAGES } from 'common/constants';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';

import { ajaxService } from '../services';

import type { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Meta surfaced on every base-query result so endpoints can read the raw response
 * (e.g. headers) in their `transformResponse`/`transformErrorResponse`. RTK Query's
 * `.unwrap()` drops `meta`, so an endpoint that needs a response value must fold it
 * onto the `data`/`error` payload itself.
 */
export type AxiosBaseQueryMeta = { response?: AxiosResponse };

interface AxiosBaseQueryArgs<
  Meta,
  Response = {
    httpStatus: 200;
    created_at: string;
  },
> {
  meta?: Meta;
  transformResponse?: (response: Response) => unknown;
}

const getRequestConfig = (args: string | AxiosRequestConfig) => {
  if (typeof args === 'string') {
    return { url: args };
  }

  return args;
};

export const axiosBaseQuery =
  <Args extends AxiosRequestConfig | string = AxiosRequestConfig, Meta = Record<string, unknown>>({
    transformResponse,
    baseURL = () => configService.getUrl(UrlNames.Api),
  }: AxiosBaseQueryArgs<Meta> & { baseURL?: string | (() => string) } = {}): BaseQueryFn<
    Args | Promise<Args>,
    unknown,
    FetchBaseQueryError,
    object,
    AxiosBaseQueryMeta
  > =>
  async (args, api, extraOptions) => {
    try {
      const requestConfig = getRequestConfig(await args);
      const resolvedBaseURL = typeof baseURL === 'function' ? baseURL() : baseURL;
      const result = await ajaxService.axios({
        baseURL: resolvedBaseURL,
        ...requestConfig,
        signal: api.signal,
        ...extraOptions,
      });

      // This base query fronts only JSON/void APIs, so an HTML body is never a legitimate success.
      // It signals an upstream/edge error served with a 200 status — e.g. a CDN returning the SPA
      // shell for a route it failed to proxy — so reject it instead of reporting silent success.
      if (typeof result.data === 'string' && /^\s*<(?:!doctype|html)/i.test(result.data)) {
        return {
          error: {
            status: result.status ?? 200,
            data: 'Unexpected HTML response (masked error)',
          },
          meta: { response: result },
        };
      }

      return {
        data: transformResponse ? transformResponse(result.data) : result.data,
        meta: { response: result },
      };
    } catch (err: unknown) {
      if (!Axios.isAxiosError(err)) {
        return {
          error: {
            status: 500,
            // @ts-expect-error add type guard
            data: err?.message || ERROR_MESSAGES.unknownError,
          },
        };
      }

      return {
        error: {
          status: err.response?.status ?? 500,
          data: err.response?.data || err.message,
        },
        meta: { response: err.response },
      };
    }
  };
